import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import { PieChart as PieIcon, Settings, AlertTriangle, Plus, Trash2 } from 'lucide-react';
import { useCurrency } from '@/contexts/CurrencyContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const getAuthHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const CATS = [
  { key: 'deposits', label: 'Deposits', color: '#9333ea' },
  { key: 'payments', label: 'Payments / Charges', color: '#dc2626' },
  { key: 'loan', label: 'Loan', color: '#0ea5e9' },
  { key: 'loan_repayment', label: 'Loan Re-payment', color: '#f59e0b' },
  { key: 'feeds', label: 'Spent on Feeds', color: '#16a34a' },
  { key: 'new_construction', label: 'New Construction', color: '#8b5cf6' },
  { key: 'maintenance', label: 'Maintenance', color: '#0d9488' },
  { key: 'miscellaneous', label: 'Miscellaneous', color: '#64748b' },
];
const SPEND_CATS = ['loan_repayment', 'feeds', 'new_construction', 'maintenance', 'miscellaneous'];
const KW_FIELDS = [
  { key: 'loan_deposit', label: 'Loan (deposits)' },
  { key: 'loan_repayment', label: 'Loan Re-payment' },
  { key: 'feeds', label: 'Spent on Feeds' },
  { key: 'new_construction', label: 'New Construction' },
  { key: 'maintenance', label: 'Maintenance' },
];

export const BankCategoryBreakdown = ({ bank, selectedDate }) => {
  const { currencySymbol } = useCurrency();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';
  const [view, setView] = useState('this');       // 'this' | 'all'
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState({ open: false, category: null, entries: [], total: 0, loading: false });
  const [kwDialog, setKwDialog] = useState(false);
  const [kwForm, setKwForm] = useState({});
  const [savingKw, setSavingKw] = useState(false);
  const [customList, setCustomList] = useState([]);
  const [newCat, setNewCat] = useState({ label: '', keywords: '' });
  const [busyCat, setBusyCat] = useState(false);

  const d = new Date(selectedDate);
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  const MONTH_NAME = d.toLocaleString('en-US', { month: 'long' });
  const activeBank = view === 'all' ? 'all' : bank;

  const fmtMoney = (n) => `${currencySymbol}${new Intl.NumberFormat('en-NG', { maximumFractionDigits: 0 }).format(Math.round(n || 0))}`;
  const compact = (n) => {
    const v = Math.round(n || 0);
    if (Math.abs(v) >= 1000000) return `${currencySymbol}${(v / 1000000).toFixed(1)}M`;
    if (Math.abs(v) >= 1000) return `${currencySymbol}${(v / 1000).toFixed(0)}k`;
    return `${currencySymbol}${v}`;
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reconciliation-category-breakdown?bank=${activeBank}&year=${year}&month=${month}`, getAuthHeader());
      setData(res.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [activeBank, year, month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totals = data?.totals || {};
  const dayLabel = (iso) => (iso ? parseInt(iso.split('-')[2], 10) : '');

  const CUSTOM_PALETTE = ['#ea580c', '#db2777', '#2563eb', '#ca8a04', '#7c3aed', '#0891b2', '#be123c', '#4d7c0f'];
  const customCats = (data?.custom_categories || []).map((c, i) => ({ key: c.key, label: c.label, color: CUSTOM_PALETTE[i % CUSTOM_PALETTE.length] }));
  const miscCat = CATS.find(c => c.key === 'miscellaneous');
  const allCats = [...CATS.filter(c => c.key !== 'miscellaneous'), ...customCats, miscCat];
  const spendKeys = ['loan_repayment', 'feeds', 'new_construction', 'maintenance', ...customCats.map(c => c.key), 'miscellaneous'];
  const colorOf = (k) => allCats.find(c => c.key === k)?.color || '#64748b';
  const labelOf = (k) => allCats.find(c => c.key === k)?.label || k;
  const uncategorized = data?.uncategorized_count || 0;

  const openDrill = async (category) => {
    setDrill({ open: true, category, entries: [], total: 0, loading: true });
    try {
      const res = await axios.get(`${API}/reconciliation-category-entries?bank=${activeBank}&year=${year}&month=${month}&category=${category}`, getAuthHeader());
      setDrill({ open: true, category, entries: res.data.entries, total: res.data.total, loading: false });
    } catch (e) {
      setDrill({ open: true, category, entries: [], total: 0, loading: false });
      toast.error('Could not load entries');
    }
  };

  const openKeywords = async () => {
    try {
      const res = await axios.get(`${API}/bank-category-keywords`, getAuthHeader());
      const form = {};
      KW_FIELDS.forEach(f => { form[f.key] = (res.data.keywords[f.key] || []).join(', '); });
      setKwForm(form);
      setCustomList((res.data.custom_categories || []).map(c => ({ key: c.key, label: c.label, keywords: (c.keywords || []).join(', ') })));
      setNewCat({ label: '', keywords: '' });
      setKwDialog(true);
    } catch (e) {
      toast.error('Could not load keywords');
    }
  };

  const addCustomCategory = async () => {
    if (!newCat.label.trim()) { toast.error('Enter a category name'); return; }
    setBusyCat(true);
    try {
      const keywords = newCat.keywords.split(',').map(s => s.trim()).filter(Boolean);
      await axios.post(`${API}/bank-custom-category`, { label: newCat.label.trim(), keywords }, getAuthHeader());
      toast.success('Category added');
      setNewCat({ label: '', keywords: '' });
      await openKeywords();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add category');
    } finally {
      setBusyCat(false);
    }
  };

  const saveCustomCategory = async (c) => {
    setBusyCat(true);
    try {
      const keywords = (c.keywords || '').split(',').map(s => s.trim()).filter(Boolean);
      await axios.put(`${API}/bank-custom-category/${c.key}`, { label: c.label, keywords }, getAuthHeader());
      toast.success('Category updated');
      fetchData();
    } catch (e) {
      toast.error('Failed to update category');
    } finally {
      setBusyCat(false);
    }
  };

  const deleteCustomCategory = async (key) => {
    if (!window.confirm('Delete this category?')) return;
    setBusyCat(true);
    try {
      await axios.delete(`${API}/bank-custom-category/${key}`, getAuthHeader());
      toast.success('Category deleted');
      await openKeywords();
      fetchData();
    } catch (e) {
      toast.error('Failed to delete');
    } finally {
      setBusyCat(false);
    }
  };

  const saveKeywords = async () => {
    setSavingKw(true);
    try {
      const keywords = {};
      KW_FIELDS.forEach(f => { keywords[f.key] = (kwForm[f.key] || '').split(',').map(s => s.trim()).filter(Boolean); });
      await axios.put(`${API}/bank-category-keywords`, { keywords }, getAuthHeader());
      toast.success('Keywords updated');
      setKwDialog(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save keywords');
    } finally {
      setSavingKw(false);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-xs">
        <div className="font-semibold text-slate-700 mb-1">{MONTH_NAME} {dayLabel(label)}, {year}</div>
        {payload.filter(p => p.value).map((p) => (
          <div key={p.dataKey} className="flex items-center justify-between gap-3">
            <span style={{ color: p.color }}>{labelOf(p.dataKey)}</span>
            <span className="font-semibold">{fmtMoney(p.value)}</span>
          </div>
        ))}
      </div>
    );
  };

  const catLabel = (k) => labelOf(k);

  return (
    <div className="card mt-6" data-testid={`bank-category-breakdown-${bank}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
            <PieIcon className="w-5 h-5" />
          </span>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Deposits &amp; Spending by Category</h2>
            <p className="text-xs text-slate-500">Auto-categorized from entry descriptions — {MONTH_NAME} {year}{view === 'all' ? ' · All banks combined' : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5" data-testid="bank-view-toggle">
            <button onClick={() => setView('this')} data-testid="bank-view-this"
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${view === 'this' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              This Bank
            </button>
            <button onClick={() => setView('all')} data-testid="bank-view-all"
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${view === 'all' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              All Banks
            </button>
          </div>
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={openKeywords} data-testid="manage-keywords-btn" className="border-slate-300">
              <Settings className="w-4 h-4 mr-1.5" /> Keywords
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <p className="text-center text-slate-400 py-8 text-sm">Loading…</p>
      ) : (
        <>
          {/* Uncategorized alert */}
          {uncategorized > 0 && (
            <div className="flex items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 mb-4" data-testid="uncategorized-alert">
              <div className="flex items-center gap-2 text-sm text-amber-800">
                <AlertTriangle className="w-5 h-5 shrink-0" />
                <span><strong>{uncategorized}</strong> payment{uncategorized > 1 ? 's' : ''} ({fmtMoney(totals.miscellaneous)}) landed in <strong>Miscellaneous</strong>. Refine keywords or add a category to classify them.</span>
              </div>
              <button onClick={() => openDrill('miscellaneous')} data-testid="review-uncategorized-btn" className="text-xs font-semibold text-amber-800 underline shrink-0 hover:text-amber-900">Review</button>
            </div>
          )}

          {/* Summary cards (clickable for drill-down) */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            {allCats.map((c) => (
              <button key={c.key} onClick={() => openDrill(c.key)}
                className="text-left rounded-xl border bg-white p-3 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer"
                style={{ borderColor: `${c.color}44` }} data-testid={`cat-total-${c.key}`}>
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color }} />
                  <span className="text-[11px] font-medium text-slate-500">{c.label}</span>
                </div>
                <div className="text-lg font-bold mt-1" style={{ color: c.color }}>{fmtMoney(totals[c.key])}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">Click to view entries</div>
              </button>
            ))}
          </div>

          {/* Daily comparison chart */}
          <div className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wider">Daily Comparison</div>
          <ResponsiveContainer width="100%" height={360}>
            <BarChart data={data?.daily || []} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="date" tickFormatter={dayLabel} tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={compact} tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} width={52} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(148,163,184,0.08)' }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="deposits" name="Deposits" fill="#9333ea" radius={[4, 4, 0, 0]} maxBarSize={14} />
              <Bar dataKey="payments" name="Payments / Charges" fill="#dc2626" radius={[4, 4, 0, 0]} maxBarSize={14} />
              {spendKeys.map((key) => (
                <Bar key={key} dataKey={key} stackId="spend" name={labelOf(key)} fill={colorOf(key)} maxBarSize={14} />
              ))}
              <Bar dataKey="loan" name="Loan (deposit)" fill="#0ea5e9" radius={[4, 4, 0, 0]} maxBarSize={14} />
            </BarChart>
          </ResponsiveContainer>
          <p className="text-[11px] text-slate-400 mt-2">The five spending categories are stacked to show total outflow per day; Deposits, Payments and Loan deposits are shown as separate bars for comparison.</p>
        </>
      )}

      {/* Drill-down dialog */}
      <Dialog open={drill.open} onOpenChange={(o) => setDrill(prev => ({ ...prev, open: o }))}>
        <DialogContent className="max-w-2xl" data-testid="category-drill-dialog">
          <DialogHeader>
            <DialogTitle>{drill.category ? catLabel(drill.category) : ''} — entries ({MONTH_NAME} {year})</DialogTitle>
          </DialogHeader>
          {drill.loading ? (
            <p className="text-center text-slate-400 py-8 text-sm">Loading…</p>
          ) : drill.entries.length === 0 ? (
            <p className="text-center text-slate-400 py-8 text-sm">No entries in this category.</p>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-sm" data-testid="drill-table">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 text-xs sticky top-0">
                    <th className="text-left font-medium px-3 py-2">Date</th>
                    {view === 'all' && <th className="text-left font-medium px-3 py-2">Bank</th>}
                    <th className="text-left font-medium px-3 py-2">Description</th>
                    <th className="text-right font-medium px-3 py-2">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {drill.entries.map((e, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{e.date}</td>
                      {view === 'all' && <td className="px-3 py-2 text-slate-500">{e.bank}</td>}
                      <td className="px-3 py-2 text-slate-700">{e.description || '—'}</td>
                      <td className="px-3 py-2 text-right font-semibold text-slate-800">{fmtMoney(e.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-200 bg-slate-50 font-bold">
                    <td className="px-3 py-2" colSpan={view === 'all' ? 3 : 2}>Total</td>
                    <td className="px-3 py-2 text-right">{fmtMoney(drill.total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Keyword editor dialog */}
      <Dialog open={kwDialog} onOpenChange={setKwDialog}>
        <DialogContent className="max-w-lg" data-testid="keywords-dialog">
          <DialogHeader>
            <DialogTitle>Manage Category Keywords</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Comma-separated keywords (matched anywhere in an entry's description, case-insensitive). Payments with no match fall under Miscellaneous.</p>
            {KW_FIELDS.map((f) => (
              <div key={f.key}>
                <label className="text-sm font-medium text-slate-700">{f.label}</label>
                <Input value={kwForm[f.key] || ''} onChange={(e) => setKwForm({ ...kwForm, [f.key]: e.target.value })}
                  placeholder="keyword1, keyword2, …" data-testid={`kw-input-${f.key}`} className="mt-1" />
              </div>
            ))}

            {/* Custom categories */}
            <div className="pt-2 border-t border-slate-200">
              <div className="text-sm font-semibold text-slate-700 mb-2">Custom Categories</div>
              {customList.length === 0 && <p className="text-xs text-slate-400 mb-2">No custom categories yet.</p>}
              <div className="space-y-2" data-testid="custom-category-list">
                {customList.map((c, idx) => (
                  <div key={c.key} className="flex items-center gap-2" data-testid={`custom-cat-${c.key}`}>
                    <Input value={c.label} onChange={(e) => { const n = [...customList]; n[idx] = { ...c, label: e.target.value }; setCustomList(n); }}
                      className="w-36 shrink-0" placeholder="Name" />
                    <Input value={c.keywords} onChange={(e) => { const n = [...customList]; n[idx] = { ...c, keywords: e.target.value }; setCustomList(n); }}
                      className="flex-1" placeholder="keywords…" />
                    <Button size="sm" variant="outline" disabled={busyCat} onClick={() => saveCustomCategory(c)} data-testid={`save-custom-${c.key}`}>Save</Button>
                    <button onClick={() => deleteCustomCategory(c.key)} data-testid={`delete-custom-${c.key}`} className="p-1.5 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
              {/* Add new */}
              <div className="flex items-center gap-2 mt-3">
                <Input value={newCat.label} onChange={(e) => setNewCat({ ...newCat, label: e.target.value })}
                  className="w-36 shrink-0" placeholder="New category" data-testid="new-cat-label" />
                <Input value={newCat.keywords} onChange={(e) => setNewCat({ ...newCat, keywords: e.target.value })}
                  className="flex-1" placeholder="keywords (comma-separated)" data-testid="new-cat-keywords" />
                <Button size="sm" disabled={busyCat} onClick={addCustomCategory} data-testid="add-custom-cat-btn" className="bg-emerald-600 hover:bg-emerald-700 shrink-0">
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setKwDialog(false)}>Cancel</Button>
            <Button onClick={saveKeywords} disabled={savingKw} data-testid="save-keywords-btn" className="bg-indigo-600 hover:bg-indigo-700">
              {savingKw ? 'Saving…' : 'Save Keywords'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BankCategoryBreakdown;
